/* eslint-disable react-hooks/exhaustive-deps */
import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import HotelIcon from "@mui/icons-material/Hotel";
import PlaceIcon from "@mui/icons-material/Place";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import PanToolIcon from "@mui/icons-material/PanTool";
import { Marker } from "react-map-gl";
import Map from "../MapWhenReady/MapWhenReady";
import "mapbox-gl/dist/mapbox-gl.css";
import gql from "graphql-tag";
import React, { useCallback, useState } from "react";
import { createAddress, editAddress } from "../../apollo/server";
import MarkerImage from "../../assets/images/marker.png";
import ConfigurableValues from "../../config/constants";
import { useTranslation } from "react-i18next";

const EDIT_ADDRESS = gql`
  ${editAddress}
`;
const CREATE_ADDRESS = gql`
  ${createAddress}
`;

function AddressDetail({
  addressDetail,
  locationModal,
  toggleDetail,
  notification,
  // Fired with the just-saved address so the caller can select it (e.g. set it
  // as the active delivery location and close the modal). Optional.
  onSaved,
}) {
  const isEdit = Boolean(addressDetail?.id);
  const { MAPBOX_TOKEN } = ConfigurableValues();
  const [addressError, setAddressError] = useState("");
  const { t } = useTranslation();

  // DoorDash-style structured fields. Prefill from the address being edited.
  const [selectedLabel, setSelectedLabel] = useState(addressDetail?.label || "");
  const [apartment, setApartment] = useState(addressDetail?.apartment || "");
  const [buildingName, setBuildingName] = useState(
    addressDetail?.buildingName || ""
  );
  const [entryCode, setEntryCode] = useState(addressDetail?.entryCode || "");
  const [dropoffOption, setDropoffOption] = useState(
    addressDetail?.dropoffOption || ""
  );
  const [instructions, setInstructions] = useState(
    addressDetail?.instructions || ""
  );

  const [mutate, { loading }] = useMutation(
    isEdit ? EDIT_ADDRESS : CREATE_ADDRESS,
    {
      onCompleted,
      onError,
    }
  );

  // Address types shown as selectable chips, each with an icon.
  const ADDRESS_TYPES = [
    { key: "Apartment", label: t("Apartment"), Icon: ApartmentIcon },
    { key: "House", label: t("House"), Icon: HomeIcon },
    { key: "Office", label: t("Office"), Icon: BusinessIcon },
    { key: "Hotel", label: t("Hotel"), Icon: HotelIcon },
    { key: "Other", label: t("Other"), Icon: PlaceIcon },
  ];

  // How the courier should hand over the order. Sent to the delivery provider
  // as the first line of the dropoff instructions.
  const DROPOFF_OPTIONS = [
    { key: "LEAVE_AT_DOOR", label: t("leaveAtDoor"), Icon: MeetingRoomIcon },
    { key: "MEET_AT_DOOR", label: t("meetAtDoor"), Icon: DoorFrontIcon },
    { key: "HAND_IT_TO_ME", label: t("handItToMe"), Icon: PanToolIcon },
  ];

  // Field labels per address type; a missing key hides that field for the type.
  const TYPE_FIELDS = {
    House: { entryCode: t("gateCode") },
    Apartment: {
      apartment: t("apartmentSuite"),
      entryCode: t("entryCode"),
      buildingName: t("buildingName"),
    },
    Hotel: {
      apartment: t("roomSuite"),
      buildingName: t("hotelName"),
    },
    Office: {
      apartment: t("suiteFloor"),
      buildingName: t("businessName"),
    },
    Other: {
      apartment: t("unitFloor"),
      entryCode: t("entryCode"),
    },
  };
  const typeFields = TYPE_FIELDS[selectedLabel] || {};

  function onCompleted(data) {
    if (isEdit) {
      notification({
        type: "warning",
        message: "Address updated",
      });
    } else {
      notification({
        type: "success",
        message: "Address added",
      });
    }
    // Hand the just-saved address back to the caller.
    //
    // On EDIT the id wins. `selected` is server-side state that editing does
    // not touch, so a customer editing address B while address A is flagged
    // selected was handed A back — checkout then showed A's details even
    // though B is what they just saved.
    //
    // On CREATE there is no id yet; the backend marks the new address selected,
    // so that flag is the only way to find it.
    if (onSaved) {
      const list =
        (data?.createAddress || data?.editAddress)?.addresses || [];
      const byId = list.find(
        (a) => String(a._id) === String(addressDetail?.id)
      );
      const saved = (isEdit && byId) || list.find((a) => a.selected) || byId || null;
      if (saved) onSaved(saved);
    }
    toggleDetail();
  }

  function onError(error) {
    console.log(error);
    notification({
      type: "error",
      message: `An error occured. Please try again. ${error}`,
    });
  }

  const clearErrors = useCallback(() => {
    setAddressError("");
  }, []);

  const validateAddress = () => {
    clearErrors();
    const detailValue = addressDetail.location;
    if (!detailValue) {
      setAddressError("Delivery details is required");
      return false;
    }
    if (!addressDetail?.lat || !addressDetail?.lng) {
      setAddressError("Delivery address is required");
      return false;
    }
    if (!selectedLabel) {
      setAddressError(t("selectLocationType"));
      return false;
    }

    // Combined `details` line for any view still reading the legacy field.
    const combined = [
      apartment,
      buildingName,
      entryCode && `Code ${entryCode}`,
    ]
      .map((x) => (x || "").trim())
      .filter(Boolean)
      .join(" · ");

    mutate({
      variables: {
        addressInput: {
          _id: isEdit ? addressDetail?.id : "",
          // Schema expects Float — coerce from whatever the picker returns.
          latitude: Number(addressDetail?.lat),
          longitude: Number(addressDetail?.lng),
          deliveryAddress: addressDetail?.location.trim(),
          details: combined,
          apartment: apartment.trim(),
          buildingName: buildingName.trim(),
          entryCode: entryCode.trim(),
          dropoffOption: dropoffOption || null,
          instructions: instructions.trim(),
          label: selectedLabel,
        },
      },
    });
  };

  // ── Design tokens ────────────────────────────────────────────────────────
  // Brand red stays the only accent; everything else is a neutral ramp so the
  // form reads as one calm surface instead of a wall of outlined red.
  const ACCENT = "#FF3008";
  const ACCENT_DARK = "#D92600";
  const ACCENT_SOFT = "#FFF3F0";
  const INK = "#111827";
  const MUTED = "#6B7280";
  const LINE = "#E8EAED";
  const FIELD_BG = "#F7F8FA";

  const sectionLabelSx = {
    fontSize: "0.7rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: MUTED,
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: FIELD_BG,
      transition: "background-color .18s ease, box-shadow .18s ease",
      "& fieldset": { borderColor: "transparent" },
      "&:hover": { backgroundColor: "#F1F3F6" },
      "&:hover fieldset": { borderColor: "transparent" },
      "&.Mui-focused": {
        backgroundColor: "#fff",
        boxShadow: `0 0 0 3px ${ACCENT_SOFT}`,
      },
      "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "1.5px" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
  };

  // Shared pill used by both the location-type and dropoff-option rows.
  const chip = ({ key, label, Icon, active, onClick }) => (
    <Button
      key={key}
      disableElevation
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      startIcon={Icon ? <Icon sx={{ fontSize: "1.05rem" }} /> : null}
      sx={{
        textTransform: "none",
        borderRadius: "999px",
        fontWeight: 700,
        fontSize: "0.82rem",
        px: 1.75,
        py: 0.75,
        border: "1.5px solid",
        borderColor: active ? ACCENT : LINE,
        color: active ? "#fff" : INK,
        backgroundColor: active ? ACCENT : "#fff",
        boxShadow: active ? `0 4px 12px ${ACCENT}33` : "none",
        transition: "all .18s ease",
        "& .MuiButton-startIcon": { mr: 0.75, color: active ? "#fff" : MUTED },
        "&:hover": {
          borderColor: ACCENT,
          backgroundColor: active ? ACCENT_DARK : ACCENT_SOFT,
          transform: "translateY(-1px)",
        },
      }}
    >
      {label}
    </Button>
  );

  return (
    <Grid
      container
      item
      xs={12}
      sx={{
        backgroundColor: "#fff",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 12px 40px rgba(17, 24, 39, 0.10)",
      }}
    >
      {/* ── Map header with the address floating over it ── */}
      <Grid item xs={12} sx={{ position: "relative", width: "100%" }}>
        <Map
          mapboxAccessToken={MAPBOX_TOKEN}
          initialViewState={{
            latitude: addressDetail?.lat,
            longitude: addressDetail?.lng,
            zoom: 17,
          }}
          style={{ height: "210px", width: "100%" }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          interactive={false}
          attributionControl={false}
          // The address card overlaps the bottom edge, so keep Mapbox's
          // wordmark (required attribution) up top where nothing covers it.
          logoPosition="top-left"
        >
          {addressDetail?.lat != null && (
            <Marker
              latitude={addressDetail.lat}
              longitude={addressDetail.lng}
              anchor="bottom"
            >
              <img src={MarkerImage} alt="marker" style={{ width: 32, height: 32 }} />
            </Marker>
          )}
        </Map>
        {/* Fade so the card below never fights the map for contrast. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(to bottom, rgba(17,24,39,0.12) 0%, rgba(17,24,39,0) 38%, rgba(255,255,255,0.55) 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: 16,
            right: 16,
            bottom: -22,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            p: 1.5,
            backgroundColor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${LINE}`,
            boxShadow: "0 8px 24px rgba(17, 24, 39, 0.12)",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 34,
              height: 34,
              borderRadius: "50%",
              backgroundColor: ACCENT_SOFT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlaceIcon sx={{ fontSize: "1.15rem", color: ACCENT }} />
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: "0.875rem",
              fontWeight: 600,
              lineHeight: 1.45,
              color: INK,
              pt: 0.4,
            }}
          >
            {addressDetail?.location ?? ""}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              e.preventDefault();
              locationModal();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              color: ACCENT,
              borderRadius: "10px",
              px: 1.25,
              minWidth: "auto",
              "&:hover": { backgroundColor: ACCENT_SOFT },
            }}
          >
            {t("edit")}
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ px: { xs: 2, sm: 2.5 }, pt: 5, pb: 2.5 }}>
        {/* ── Location type selector ── */}
        <Typography sx={sectionLabelSx}>{t("locationType")}</Typography>
        <Typography
          sx={{ fontSize: "0.8rem", color: MUTED, mt: 0.5, mb: 1.5 }}
        >
          {t("locationTypeDetails")}
        </Typography>
        <Box display="flex" flexWrap="wrap" sx={{ gap: 1 }}>
          {ADDRESS_TYPES.map(({ key, label, Icon }) =>
            chip({
              key,
              label,
              Icon,
              active: selectedLabel === key,
              onClick: () => setSelectedLabel(key),
            })
          )}
        </Box>

        {/* ── Per-type structured fields + delivery instructions ── */}
        {selectedLabel ? (
          <>
            <Box
              sx={{
                mt: 2.5,
                pt: 2.5,
                borderTop: `1px solid ${LINE}`,
              }}
            >
              {typeFields.apartment ? (
                <TextField
                  variant="outlined"
                  label={typeFields.apartment}
                  value={apartment}
                  fullWidth
                  margin="dense"
                  inputProps={{ maxLength: 60 }}
                  onChange={(e) => setApartment(e.target.value)}
                  InputLabelProps={{ style: { color: MUTED } }}
                  sx={fieldSx}
                />
              ) : null}
              {typeFields.buildingName ? (
                <TextField
                  variant="outlined"
                  label={typeFields.buildingName}
                  value={buildingName}
                  fullWidth
                  margin="dense"
                  inputProps={{ maxLength: 80 }}
                  onChange={(e) => setBuildingName(e.target.value)}
                  InputLabelProps={{ style: { color: MUTED } }}
                  sx={fieldSx}
                />
              ) : null}
              {typeFields.entryCode ? (
                <TextField
                  variant="outlined"
                  label={typeFields.entryCode}
                  value={entryCode}
                  fullWidth
                  margin="dense"
                  inputProps={{ maxLength: 40 }}
                  onChange={(e) => setEntryCode(e.target.value)}
                  InputLabelProps={{ style: { color: MUTED } }}
                  sx={fieldSx}
                />
              ) : null}
            </Box>

            {/* ── Dropoff option ── */}
            <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${LINE}` }}>
              <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>
                {t("dropoffOption")}
              </Typography>
              <Box display="flex" flexWrap="wrap" sx={{ gap: 1 }}>
                {DROPOFF_OPTIONS.map(({ key, label, Icon }) =>
                  chip({
                    key,
                    label,
                    Icon,
                    active: dropoffOption === key,
                    // Tapping the active chip clears it — the option is optional.
                    onClick: () =>
                      setDropoffOption(dropoffOption === key ? "" : key),
                  })
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${LINE}` }}>
              <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>
                {t("deliveryInstructions")}
              </Typography>
              <TextField
                variant="outlined"
                placeholder={t("deliveryInstructionsHint")}
                value={instructions}
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 250 }}
                onChange={(e) => setInstructions(e.target.value)}
                sx={fieldSx}
              />
            </Box>
          </>
        ) : null}

        {addressError ? (
          <Box
            sx={{
              mt: 2,
              px: 1.75,
              py: 1.25,
              borderRadius: "12px",
              backgroundColor: ACCENT_SOFT,
              border: `1px solid ${ACCENT}33`,
            }}
          >
            <Typography
              sx={{ fontSize: "0.82rem", fontWeight: 600, color: ACCENT_DARK }}
            >
              {addressError}
            </Typography>
          </Box>
        ) : null}

        <Button
          disabled={loading}
          fullWidth
          disableElevation
          onClick={(e) => {
            e.preventDefault();
            validateAddress();
          }}
          sx={{
            mt: 3,
            height: 54,
            borderRadius: "14px",
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 800,
            color: "#fff",
            backgroundColor: ACCENT,
            boxShadow: `0 8px 20px ${ACCENT}40`,
            transition: "all .18s ease",
            "&:hover": {
              backgroundColor: ACCENT_DARK,
              boxShadow: `0 10px 26px ${ACCENT}55`,
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#F1F3F6",
              color: MUTED,
              boxShadow: "none",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: MUTED }} />
          ) : (
            t("submit")
          )}
        </Button>
      </Grid>
    </Grid>
  );
}

export default React.memo(AddressDetail);